import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface FreeDaySlot {
    label: string;
    timestamp: number;
    availableMembers: Array<{
        memberId: number;
        // name/email are optional: the inline booking card receives memberId-only
        // slots (see get-calendar-availability.server.ts). Conversion just passes
        // the members through unchanged.
        name?: string;
        email?: string;
    }>;
}

interface FreeDay {
    date: string;
    freeSlots: FreeDaySlot[];
}

/**
 * Convert free days from one timezone to another.
 * Timestamps remain the same (UTC), but date strings and labels are converted.
 * Mirrors the PHP CalendarAvailabilityService::convertFreeDaysToTimezone logic.
 *
 * @param freeDays - Free days with slots (from any timezone in the response)
 * @param targetTimezone - Target timezone identifier (e.g. "Europe/London")
 * @returns Free days grouped by local date in target timezone
 */
export function convertFreeDaysToTimezone(
    freeDays: FreeDay[],
    targetTimezone: string,
): FreeDay[] {
    const slotsByDate: Record<string, FreeDaySlot[]> = {};

    for (const day of freeDays) {
        for (const slot of day.freeSlots) {
            const dateInTarget = dayjs(slot.timestamp).tz(targetTimezone);
            const dateKey = dateInTarget.format('YYYY-MM-DD');

            let arr = slotsByDate[dateKey];
            if (!arr) {
                arr = [];
                slotsByDate[dateKey] = arr;
            }
            arr.push({
                label: dateInTarget.format('h:mm A'),
                timestamp: slot.timestamp,
                availableMembers: slot.availableMembers,
            });
        }
    }

    const sortedDates = Object.keys(slotsByDate).sort();
    return sortedDates.map((dateKey) => {
        const arr = slotsByDate[dateKey];
        const slots = (arr ?? []).sort((a, b) => a.timestamp - b.timestamp);
        return { date: dateKey, freeSlots: slots };
    });
}
